"use client";

/**
 * "Is this your listing?" on /elan: an owner or an agent who lands on
 * their own listing adds it to /listing-analytics in one tap (signed
 * out: sign in first, then back here).
 */
import { useState } from "react";
import { LINK } from "@/lib/ui-classes";
import Link from "@/lib/localized-link";
import { useAuth } from "@/lib/auth-context";
import { useT } from "@/lib/locale-context";
import type { TranslationKey } from "@/lib/i18n";
import { trackEvent } from "@/lib/tracking";
import { useTracked, useTrackedRef } from "@/lib/use-tracked";

export default function TrackThisListing({ url, path, compact = false }: { url: string; path: string; compact?: boolean }) {
  const t = useT();
  const { user } = useAuth();
  const { add } = useTracked({ lazy: true });
  // One already in the analytics shows the link, not the add button
  // again (owner, 2026-10-10: "I have already added the listing, why do
  // I still see it in a hanging action?").
  const ref = useTrackedRef(user ? url : null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [added, setAdded] = useState(false);
  const tracked = added || !!ref.tracked;
  if (user && !ref.known) return null;

  return (
    <div className={compact ? "rounded-xl border border-gray-200 bg-white px-3.5 py-3" : "rounded-2xl border border-gray-200 bg-white p-4"}>
      <p className="text-sm font-semibold text-gray-900">{tracked ? t("la.mine.in") : t("la.mine.title")}</p>
      {!tracked && <p className="mt-0.5 text-sm text-gray-600">{t("la.mine.sub")}</p>}
      {tracked ? (
        <Link href="/listing-analytics" className={`mt-2 inline-flex text-sm ${LINK}`}>
          {t("la.mine.open")}
        </Link>
      ) : user ? (
        <button
          type="button"
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            setErr(null);
            const r = await add(url);
            setBusy(false);
            trackEvent("analytics.add", { ok: r.ok ? 1 : 0, src: "elan" });
            if (r.ok) { setAdded(true); void ref.refreshIds(); }
            else setErr(r.code);
          }}
          className="mt-3 inline-flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-300 disabled:opacity-60"
        >
          {busy && <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" aria-hidden />}
          {t("la.mine.cta")}
        </button>
      ) : (
        <Link
          href={`/auth/login?return_to=${encodeURIComponent(path)}`}
          rel="nofollow"
          className="mt-3 inline-flex rounded-xl border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-900 transition-colors hover:border-gray-300"
        >
          {t("la.mine.cta")}
        </Link>
      )}
      {err && <p className="mt-2 text-sm text-rose-700" role="alert">{t(`la.err.${err}` as TranslationKey).replace("{n}", "200")}</p>}
    </div>
  );
}
